import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
    Typography,
    Card,
    CardContent,
    Grid,
    Box,
    Button,
    Paper,
    Avatar,
    CircularProgress,
    IconButton,
    LinearProgress,
    TextField
} from '@mui/material';
import dayjs from 'dayjs';
import { DataGrid } from '@mui/x-data-grid';
import { HourglassEmpty, AccessTime, People, Done } from '@mui/icons-material';
import { t } from 'i18next';
import Visibility from '@mui/icons-material/Visibility';
import { fetchGroupByUUID } from '../../../services/groups';
import { useRecords } from '../../../hooks/useRecords';
import { formatDateMoment, getHoursAndMinutes } from '../../../utils/dateUtils';
import { SeeButton, SelectionButton } from '../../../component/buttons';
import {
    fetchWeeklyComputations,
    fetchWeeklyGroupComputations
} from '../../../services/computations';
import WeeklyProgressChart from '../SeeUser/components/WeeklyProgressChart';
import ExerciseRatioPie from '../SeeUser/components/ExerciseRatioPie';
import ExerciseTypesPie from '../SeeUser/components/ExerciseTypesPie';
import UserAlertGrid from '../../dashboard/Medic/UserAlertGrid';
import { sendMessage } from '../../../services/chat';
import PatientCard from './components/PatientCard';

const RendEditCell = (params) => {
    const record = params.row;
    const navigate = useNavigate();
    const seeText = t('users.see');

    return (
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
            <SeeButton
                onClick={() => navigate('/users/user/record', { state: { record } })}
                size="small"
            >
                <Typography variant="subtitle2">{seeText}</Typography>
            </SeeButton>
        </Grid>
    );
};

const getProgress = (start, end) => {
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    const today = dayjs();
    const total = endDate.diff(startDate, 'day');
    const current = today.diff(startDate, 'day');
    if (total <= 0 || current >= total) return 100;
    if (current <= 0) return 0;
    return Math.round((current / total) * 100);
};

const GroupDetails = () => {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(true);

    const [weeklyDataList, setWeeklyDataList] = useState([]);

    const [selectedPatient, setSelectedPatient] = useState(null);

    const [groupMessage, setGroupMessage] = useState('');
    const [sendingGroupMessage, setSendingGroupMessage] = useState(false);
    const [messageSuccess, setMessageSuccess] = useState(false);

    const [patientWeeklyData, setPatientWeeklyData] = useState({});
    const [loadingPatient, setLoadingPatient] = useState(false);

    const activeWeeklyData = selectedPatient
        ? patientWeeklyData[selectedPatient.uuid] || []
        : weeklyDataList;

    const [selectedAlert, setSelectedAlert] = useState(0);

    const alertSections = [t('dashboard.alerts.new'), t('dashboard.alerts.analyzed')];

    const sections = [
        t('groups.sections.stats'),
        t('groups.sections.alertas'),
        t('groups.sections.exercices')
    ];

    const [selected, setSelected] = useState(0);

    const tableConfig = {
        columns: [
            {
                field: 'patient',
                headerName: t('groups.table_patient'),
                flex: 1,
                valueGetter: (patient) => patient?.name
            },
            {
                field: 'config',
                headerName: t('groups.table_exercise_type'),
                flex: 1,
                valueGetter: (config) =>
                    TYPE_LABELS[config?.exerciseType] || config?.exerciseType || ''
            },
            {
                field: 'ti',
                headerName: t('groups.table_date'),
                headerAlign: 'center',
                display: 'flex',
                align: 'center',
                width: 150,
                valueGetter: (ti) => formatDateMoment(ti)
            },
            {
                field: 'hora',
                headerName: t('groups.table_hour'),
                headerAlign: 'center',
                display: 'flex',
                align: 'center',
                width: 150,
                renderCell: (params) => getHoursAndMinutes(params.row?.ti || '')
            },
            {
                field: 'duration',
                headerAlign: 'center',
                headerName: t('groups.table_duration'),
                display: 'flex',
                align: 'center',
                flex: 1,
                valueGetter: (duration) => `${duration} min`
            },
            {
                field: 'mHR',
                headerName: t('groups.table_heart_rate'),
                headerAlign: 'center',
                flex: 1,
                display: 'flex',
                align: 'center',
                valueGetter: (mHR) => `${mHR} Bpm`
            },
            {
                field: 'edit',
                headerName: t('groups.table_actions'),
                headerAlign: 'center',
                display: 'flex',
                align: 'center',
                width: 250,
                renderCell: RendEditCell
            }
        ]
    };

    const totalExercises = activeWeeklyData.reduce((sum, week) => {
        return (
            sum +
            Object.values(week.data.exercises?.done_in_plan || {}).reduce(
                (a, b) => a + b,
                0
            )
        );
    }, 0);

    const totalSkipped = activeWeeklyData.reduce((sum, week) => {
        return (
            sum +
            Object.values(week.data.exercises?.pending_in_plan || {}).reduce(
                (a, b) => a + b,
                0
            )
        );
    }, 0);

    const aggregatedTypes = {};
    weeklyDataList.forEach((week) => {
        const total = week.data.exercises?.total || {};
        Object.entries(total).forEach(([type, count]) => {
            if (!aggregatedTypes[type]) aggregatedTypes[type] = 0;
            aggregatedTypes[type] += count;
        });
    });

    const exerciseTypes = Object.entries(aggregatedTypes).map(([name, value]) => ({
        name,
        value
    }));

    const weeklyStats = activeWeeklyData.map((week) => {
        const done = Object.values(week.data.exercises?.done_in_plan || {}).reduce(
            (a, b) => a + b,
            0
        );
        const skipped = Object.values(week.data.exercises?.pending_in_plan || {}).reduce(
            (a, b) => a + b,
            0
        );

        return {
            week: week.week,
            done,
            skipped
        };
    });

    const ratio =
        totalExercises === 0 && totalSkipped === 0
            ? t('groups.no_exercises')
            : totalSkipped === 0
              ? '100%'
              : `${Math.round(
                    (totalExercises / (totalExercises + totalSkipped)) * 100
                )}%`;

    const {
        data,
        isLoading,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    } = useRecords(null, groupId);

    useEffect(() => {
        (async () => {
            try {
                const data = await fetchGroupByUUID(groupId);
                setGroup(data);
            } catch (err) {
                console.error('Erro ao procurar grupo:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, [groupId]);

    useEffect(() => {
        const fetchWeeklyData = async () => {
            if (!groupId) return;

            try {
                let weeks = [];

                const start = dayjs(group.start_date);
                const end = dayjs(group.end_date);

                const numWeeks = Math.ceil(end.diff(start, 'week', true));
                weeks = [...Array(numWeeks)].map((_, i) =>
                    start.add(i, 'week').startOf('week').format('YYYY-MM-DD')
                );

                const results = await Promise.all(
                    weeks.map((weekStart) =>
                        fetchWeeklyGroupComputations({
                            qi: weekStart,
                            group: groupId
                        }).then((data) => ({
                            week: dayjs(weekStart).format('DD-MM'),
                            data
                        }))
                    )
                );

                setWeeklyDataList(results);
            } catch (err) {
                console.error('Erro ao procurar dados das semanas:', err);
            }
        };

        fetchWeeklyData();
    }, [groupId, group]);

    const loadPatientWeeklyData = async (patientUuid) => {
        if (patientWeeklyData[patientUuid]) return;

        setLoadingPatient(true);

        try {
            const start = dayjs(group.start_date);
            const end = dayjs(group.end_date);
            const numWeeks = Math.ceil(end.diff(start, 'week', true));

            const weeks = [...Array(numWeeks)].map((_, i) =>
                start.add(i, 'week').startOf('week').format('YYYY-MM-DD')
            );

            const results = await Promise.all(
                weeks.map((weekStart) =>
                    fetchWeeklyComputations({
                        qi: weekStart,
                        patient: patientUuid
                    }).then((data) => ({
                        week: dayjs(weekStart).format('DD-MM'),
                        data
                    }))
                )
            );

            setPatientWeeklyData((prev) => ({
                ...prev,
                [patientUuid]: results
            }));
        } finally {
            setLoadingPatient(false);
        }
    };

    const handleSelectPatient = (patient) => {
        if (selectedPatient?.uuid === patient.uuid) {
            setSelectedPatient(null);
            return;
        }

        setSelectedPatient(patient);
        loadPatientWeeklyData(patient.uuid);
    };

    const handleSendGroupMessage = async () => {
        if (!groupMessage.trim()) return;

        setSendingGroupMessage(true);

        try {
            const patients = group?.patients || [];

            await Promise.all(
                patients.map((patient) =>
                    sendMessage({
                        to: patient.uuid,
                        message: groupMessage
                    })
                )
            );

            setGroupMessage('');
            setMessageSuccess(true);
        } catch (err) {
            console.error('Erro ao enviar mensagem para o grupo:', err);
        } finally {
            setSendingGroupMessage(false);
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="200px"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!group) {
        return <Typography variant="h6">{t('groups.not_found')}</Typography>;
    }

    if (group.patients.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h4" gutterBottom color="text.secondary">
                            {group.name}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                            {group.description}
                        </Typography>
                        <Typography variant="caption">
                            {dayjs(group.start_date).format('DD/MM/YYYY')} –{' '}
                            {dayjs(group.end_date).format('DD/MM/YYYY')}
                        </Typography>
                    </CardContent>
                </Card>

                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 6, textAlign: 'center' }}
                >
                    <Typography variant="h6" gutterBottom color="text.secondary">
                        {t('groups.no_patients')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        onClick={() => navigate(`/grupos/editar/${group.uuid}`)}
                    >
                        {t('groups.add_patients')}
                    </Button>
                </Box>
            </Box>
        );
    }

    const progress = getProgress(group.start_date, group.end_date);

    const TYPE_LABELS = {
        rehabilitation: t('exercise.types.rehabilitation'),
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other')
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Card sx={{ mb: 4, borderRadius: 4 }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                    >
                        <Grid item xs={12} md={6}>
                            <Typography variant="h2" fontWeight={700} color="#0A579B">
                                {group.name}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                                {dayjs(group.start_date).format('DD/MM/YYYY')} –{' '}
                                {dayjs(group.end_date).format('DD/MM/YYYY')}
                            </Typography>
                        </Grid>

                        {/* Stats */}
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2} justifyContent="flex-end">
                                {[
                                    {
                                        icon: <People fontSize="large" color="primary" />,
                                        value: group?.patients?.length || 0,
                                        label: t('groups.label_members')
                                    },
                                    {
                                        icon: <Done fontSize="large" color="primary" />,
                                        value: totalExercises,
                                        label: t('groups.label_exercises_completed')
                                    },
                                    {
                                        icon: (
                                            <AccessTime
                                                fontSize="large"
                                                color="primary"
                                            />
                                        ),
                                        value: totalSkipped,
                                        label: t('groups.label_exercises_skipped')
                                    }
                                ].map((stat, i) => (
                                    <Grid item key={i}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                px: 2.5,
                                                py: 1.5,
                                                borderRadius: 3,
                                                backgroundColor: 'grey.50',
                                                minWidth: 250,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5
                                            }}
                                        >
                                            {stat.icon}
                                            <Box>
                                                <Typography fontWeight={700}>
                                                    {stat.value}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {stat.label}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Progress bar */}
                    <Box sx={{ mt: 3 }}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.5}
                        >
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('groups.label_progress')}
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                fontWeight={1000}
                                color="primary.main"
                            >
                                {progress}%
                            </Typography>
                        </Box>

                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 5
                                }
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {sections.map((section, index) => (
                    <Grid item key={index} xs={4}>
                        <SelectionButton
                            selected={selected === index}
                            sx={{ width: '100%', py: 2 }}
                            onClick={() => {
                                setSelected(index);
                            }}
                        >
                            {section}
                        </SelectionButton>
                    </Grid>
                ))}
            </Grid>

            {selected === 0 && (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <WeeklyProgressChart
                                weeklyStats={weeklyStats}
                                patientName={selectedPatient?.name || null}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <ExerciseRatioPie
                                        totalExercises={totalExercises}
                                        totalSkipped={totalSkipped}
                                        ratio={ratio}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <ExerciseTypesPie exerciseTypes={exerciseTypes} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                {t('groups.patients')}
                            </Typography>
                            <Box
                                sx={{
                                    height: 3,
                                    width: 70,
                                    backgroundColor: '#1976d2',
                                    borderRadius: 2,
                                    mb: 2
                                }}
                            />
                            <Grid container spacing={2}>
                                {(group?.patients || []).map((p) => (
                                    <Grid item xs={12} md={4} key={p.uuid}>
                                        <PatientCard
                                            patient={p}
                                            selected={selectedPatient?.uuid === p.uuid}
                                            onSelect={handleSelectPatient}
                                            navigate={navigate}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                {t('groups.send_message')}
                            </Typography>

                            <Box
                                sx={{
                                    height: 3,
                                    width: 190,
                                    backgroundColor: '#1976d2',
                                    borderRadius: 2,
                                    mb: 3
                                }}
                            />

                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                maxRows={8}
                                placeholder={t('groups.write_message')}
                                value={groupMessage}
                                onChange={(e) => setGroupMessage(e.target.value)}
                                disabled={sendingGroupMessage}
                            />

                            <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                    variant="contained"
                                    onClick={handleSendGroupMessage}
                                    disabled={sendingGroupMessage || !groupMessage.trim()}
                                >
                                    {sendingGroupMessage
                                        ? t('groups.sending')
                                        : t('groups.send_to_all')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </>
            )}

            {selected === 1 && (
                <>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                {alertSections.map((value, index) => (
                                    <Grid item xs={6} key={index}>
                                        <SelectionButton
                                            selected={selectedAlert === index}
                                            sx={{ width: '100%', py: 1.5 }}
                                            onClick={() => setSelectedAlert(index)}
                                        >
                                            {value}
                                        </SelectionButton>
                                    </Grid>
                                ))}

                                <Grid item xs={12} mt={2}>
                                    <UserAlertGrid type={selectedAlert} group={groupId} />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </>
            )}

            {selected === 2 && (
                <>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                {t('groups.latest_exercises')}
                            </Typography>
                            <Box
                                sx={{
                                    height: 3,
                                    width: 130,
                                    backgroundColor: '#1976d2',
                                    borderRadius: 2,
                                    mb: 2
                                }}
                            />
                            <DataGrid
                                paginationMode="server"
                                style={{ width: '100%' }}
                                density="standard"
                                loading={isLoading}
                                rows={data}
                                getRowId={(row) => row.uuid}
                                columns={tableConfig.columns}
                                pageSizeOptions={rowsPerPageOptions}
                                rowCount={count}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                            />
                        </CardContent>
                    </Card>
                </>
            )}
            <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button variant="outlined" onClick={() => navigate('/grupos')}>
                    {t('groups.back')}
                </Button>
            </Box>
        </Box>
    );
};

export default GroupDetails;
